/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import storage from 'embercom/vendor/intercom/storage';

export enum HandlingEventType {
  Resume = 'resume',
  Pause = 'pause',
}

export enum PauseReason {
  ConversationSwitch = 'conversation_switch',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

interface StoredHandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: string;
  reason?: PauseReason;
}

const STORAGE_KEY_PREFIX = 'inbox.handling_events';
const ACTIVE_CONVERSATION_KEY_PREFIX = 'inbox.active_handling_conversation';

export default class ConversationHandlingService extends Service {
  @service declare session: Session;

  @tracked private activeConversationId: number | null = null;
  @tracked pendingEvents: HandlingEvent[] = [];

  get storageKey(): string {
    return `${STORAGE_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  get activeConversationStorageKey(): string {
    return `${ACTIVE_CONVERSATION_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  constructor() {
    super(...arguments);
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      // Load pending events (scoped by workspace and teammate)
      let eventsJson = storage.get(this.storageKey);
      if (eventsJson) {
        let storedEvents = JSON.parse(eventsJson) as StoredHandlingEvent[];
        this.pendingEvents = storedEvents.map((e) => ({
          conversationId: e.conversationId,
          type: e.type,
          timestamp: new Date(e.timestamp),
          reason: e.reason,
        }));
      }

      // Load active conversation (scoped by workspace and teammate)
      let activeConvId = storage.get(this.activeConversationStorageKey);
      if (activeConvId) {
        this.activeConversationId = Number(activeConvId);
        // eslint-disable-next-line no-console
        console.log('[AHT] Restored active conversation', {
          conversationId: this.activeConversationId,
          workspaceId: this.session.workspace.id,
          teammateId: this.session.teammate.id,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to load from storage', error);
      // Clear corrupted data
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    try {
      // Save pending events (scoped by workspace and teammate)
      let storedEvents: StoredHandlingEvent[] = this.pendingEvents.map((e) => ({
        conversationId: e.conversationId,
        type: e.type,
        timestamp: e.timestamp.toISOString(),
        reason: e.reason,
      }));
      storage.set(this.storageKey, JSON.stringify(storedEvents));

      // Save active conversation (scoped by workspace and teammate)
      if (this.activeConversationId) {
        storage.set(this.activeConversationStorageKey, String(this.activeConversationId));
      } else {
        storage.remove(this.activeConversationStorageKey);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to save to storage', error);
    }
  }

  private clearStorage(): void {
    storage.remove(this.storageKey);
    storage.remove(this.activeConversationStorageKey);
    this.pendingEvents = [];
  }

  private shouldTrackConversation(conversation: Conversation): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;
    let isValidType =
      !conversation.isBackOfficeTicket &&
      !conversation.isSideConversation &&
      !conversation.isTrackerTicket;

    return isOpen && isAssignedToMe && isValidType;
  }

  private createResumeEvent(conversationId: number): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Resume,
      timestamp: new Date(),
    };

    this.pendingEvents.push(event);
    this.activeConversationId = conversationId;
    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Resume event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Pause,
      timestamp: new Date(),
      reason,
    };

    this.pendingEvents.push(event);

    if (this.activeConversationId === conversationId) {
      this.activeConversationId = null;
    }

    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Pause event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      reason: reason || 'none',
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  resumeConversation(conversation: Conversation): void {
    let isTrackable = this.shouldTrackConversation(conversation);

    // Don't create duplicate resume if already viewing this trackable conversation
    if (isTrackable && this.activeConversationId === conversation.id) {
      return;
    }

    // Pause the currently active conversation when switching to a different conversation
    if (this.activeConversationId && this.activeConversationId !== conversation.id) {
      this.createPauseEvent(this.activeConversationId, PauseReason.ConversationSwitch);
    }

    // Create resume event only for trackable conversations
    if (isTrackable) {
      this.createResumeEvent(conversation.id);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
